import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid-premium";
import { SafeDatePicker } from "components/SafeDatePicker";
import { Fetching } from "components/common/loading/fetching";
import { formatArea } from "components/identifyEventsTool/utils/formatArea";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMaterials } from "hooks/useMaterials";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useGetCostLabellersQuery } from "src/store/api/generatedApi";
import { AccuracyEventsTable } from "./AccuracyEventsTable";
import { AccuracyMatrix } from "./AccuracyMatrix";
import { useLabellerAccuracyData } from "./useLabellerAccuracyData";
import {
  AccuracyMetric,
  buildAccuracyPageUrl,
  isAccuracyMetric,
} from "./utils";

dayjs.extend(utc);

const DEFAULT_METRIC: AccuracyMetric = "material";

export const LabellerAccuracyPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const navigate = useNavigate();
  const { labellerId: rawLabellerId } = useParams<{ labellerId: string }>();
  const labellerId = rawLabellerId ?? "";
  const [searchParams] = useSearchParams();

  const metricParam = searchParams.get("metric") ?? DEFAULT_METRIC;
  const metric: AccuracyMetric = isAccuracyMetric(metricParam)
    ? metricParam
    : DEFAULT_METRIC;
  const location = searchParams.get("location") ?? "";
  const startParam = searchParams.get("start");
  const endParam = searchParams.get("end");
  const predicted = searchParams.get("predicted") ?? "";
  const actual = searchParams.get("actual") ?? "";

  const start = useMemo(() => {
    if (!startParam) return null;
    const parsed = dayjs.utc(startParam);
    return parsed.isValid() ? parsed : null;
  }, [startParam]);
  const end = useMemo(() => {
    if (!endParam) return null;
    const parsed = dayjs.utc(endParam);
    return parsed.isValid() ? parsed : null;
  }, [endParam]);

  const { data: labellers, isLoading: isLoadingLabellers } =
    useGetCostLabellersQuery({ tenantName });

  const { locationNames } = useLabellingLocations();

  const labeller = useMemo(
    () => labellers?.find((entry) => entry.id === labellerId) ?? null,
    [labellers, labellerId]
  );

  const { materials } = useMaterials();
  const formatMaterialLabel = useMemo(() => {
    const nameToLabellingName = new Map(
      (materials ?? []).map((material) => [
        material.name,
        material.labelling_name ?? material.name,
      ])
    );
    return (name: string) => nameToLabellingName.get(name) ?? name;
  }, [materials]);

  const formatLabel = metric === "area" ? formatArea : formatMaterialLabel;

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const filterSignature = `${metric}|${predicted}|${actual}|${location}|${start?.valueOf() ?? ""}|${end?.valueOf() ?? ""}`;
  const [lastFilterSignature, setLastFilterSignature] =
    useState(filterSignature);
  if (filterSignature !== lastFilterSignature) {
    setLastFilterSignature(filterSignature);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }

  const {
    matrixEntries,
    eventRows,
    totalRowCount,
    isFetching: isFetchingComparison,
    error: comparisonError,
  } = useLabellerAccuracyData({
    tenantName,
    labellerId,
    location,
    metric,
    predicted,
    actual,
    start,
    end,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
  });

  const buildCellHref = (cellPredicted: string, cellActual: string) =>
    buildAccuracyPageUrl({
      tenantName,
      labellerId,
      metric,
      predicted: cellPredicted,
      actual: cellActual,
      location,
      start,
      end,
    });

  const handleMetricChange = (
    _: React.MouseEvent<HTMLElement>,
    next: string | null
  ) => {
    if (!next || !isAccuracyMetric(next) || next === metric) return;
    void navigate(
      buildAccuracyPageUrl({
        tenantName,
        labellerId,
        metric: next,
        location,
        start,
        end,
      })
    );
  };

  const handleLocationChange = (next: string) => {
    if (!next || next === location) return;
    void navigate(
      buildAccuracyPageUrl({
        tenantName,
        labellerId,
        metric,
        predicted,
        actual,
        location: next,
        start,
        end,
      })
    );
  };

  const handleStartChange = (next: Dayjs | null) => {
    if (next !== null && !next.isValid()) return;
    void navigate(
      buildAccuracyPageUrl({
        tenantName,
        labellerId,
        metric,
        predicted,
        actual,
        location,
        start: next,
        end,
      })
    );
  };

  const handleEndChange = (next: Dayjs | null) => {
    if (next !== null && !next.isValid()) return;
    void navigate(
      buildAccuracyPageUrl({
        tenantName,
        labellerId,
        metric,
        predicted,
        actual,
        location,
        start,
        end: next,
      })
    );
  };

  const showMatrix = metric !== "timing";
  const cellExistsInMatrix = useMemo(
    () =>
      matrixEntries.some(
        (entry) => entry.predicted === predicted && entry.actual === actual
      ),
    [matrixEntries, predicted, actual]
  );
  const selectedCell =
    showMatrix && predicted && actual && cellExistsInMatrix
      ? { predicted, actual }
      : null;
  const cellRequestedButMissing =
    showMatrix &&
    !!predicted &&
    !!actual &&
    !cellExistsInMatrix &&
    !isFetchingComparison &&
    !!labeller;
  const showEventsTable = metric === "timing" || selectedCell !== null;

  if (!isLoadingLabellers && !labeller) {
    return (
      <Stack spacing={2} sx={{ p: 3 }}>
        <Alert severity="error">{t("labellerNotFound")}</Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: 3, flex: 1, overflow: "auto" }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <IconButton
          component={Link}
          to={`/${tenantName}/manage-labellers`}
          aria-label={t("backToManageLabellers")}
        >
          <ArrowBackIcon />
        </IconButton>
        <Stack>
          <Typography variant="h5">{labeller?.name ?? labellerId}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("labellerAccuracyDrilldown")}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          size="small"
          value={metric}
          exclusive
          onChange={handleMetricChange}
        >
          <ToggleButton value="material">{t("material")}</ToggleButton>
          <ToggleButton value="area">{t("area")}</ToggleButton>
          <ToggleButton value="timing">{t("timing")}</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="accuracy-location-select-label" shrink>
            {t("location")}
          </InputLabel>
          <Select
            labelId="accuracy-location-select-label"
            value={location}
            label={t("location")}
            notched
            displayEmpty
            onChange={(e) => {
              handleLocationChange(e.target.value);
            }}
          >
            <MenuItem value="" disabled>
              {t("pickALocation")}
            </MenuItem>
            {locationNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SafeDatePicker
          label={t("startNoun")}
          value={start}
          onChange={handleStartChange}
          slotProps={{
            textField: { size: "small", sx: { minWidth: 150 } },
            field: { clearable: true },
          }}
          format="YYYY-MM-DD"
        />
        <Typography variant="body2" color="text.secondary">
          –
        </Typography>
        <SafeDatePicker
          label={t("endNoun")}
          value={end}
          onChange={handleEndChange}
          slotProps={{
            textField: { size: "small", sx: { minWidth: 150 } },
            field: { clearable: true },
          }}
          format="YYYY-MM-DD"
        />
      </Stack>

      {!location ? (
        <Alert severity="info">{t("pickLocationToSeeAccuracy")}</Alert>
      ) : null}

      {location && comparisonError ? (
        <Alert severity="error">{t("loadAccuracyError")}</Alert>
      ) : null}

      {location && !comparisonError && cellRequestedButMissing ? (
        <Alert severity="warning">{t("cellMissingFromCurrentMatrix")}</Alert>
      ) : null}

      {location && !comparisonError ? (
        <Fetching
          fetching={isFetchingComparison}
          sx={{
            minHeight: 200,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {showMatrix && (matrixEntries.length > 0 || !isFetchingComparison) ? (
            <Box>
              <AccuracyMatrix
                entries={matrixEntries}
                formatLabel={formatLabel}
                selectedCell={selectedCell}
                buildCellHref={buildCellHref}
              />
              {!selectedCell &&
              !cellRequestedButMissing &&
              matrixEntries.length > 0 ? (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  {t("selectCellToSeeEvents")}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {showEventsTable && labeller ? (
            <Box sx={{ flex: 1, minHeight: 360 }}>
              <AccuracyEventsTable
                rows={eventRows}
                rowCount={totalRowCount}
                loading={isFetchingComparison}
                tenantName={tenantName}
                location={location}
                labeller={labeller}
                formatLabel={formatLabel}
                showLabels={metric !== "timing"}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
              />
            </Box>
          ) : null}
        </Fetching>
      ) : null}
    </Stack>
  );
};
